import React, { useState, useEffect } from "react";
import AdminLayout from "../Layouts/AdminLayout";
import { Typography, Card, message, Skeleton, Row, Col, Table, Tag, Button } from "antd";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const { Text } = Typography;

const PALETTE = [
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
];

const ticketStatusColors = {
  Open: "#3b82f6",
  "Waiting for manager reply": "#f59e0b",
  "Waiting for customer reply": "#8b5cf6",
  Closed: "#10b981",
};

const appointmentStatusColors = {
  Pending: "#f59e0b",
  Confirmed: "#3b82f6",
  confirmed: "#3b82f6",
  Completed: "#10b981",
  Cancelled: "#ef4444",
  cancelled: "#ef4444",
};

const colorFor = (key, map) => map[key] || PALETTE[0];

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "bottom" },
  },
};

const barOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    y: { beginAtZero: true, ticks: { precision: 0 } },
  },
};

const statusTagColor = (status) =>
  status === "Open" ? "green" : status === "Closed" ? "red" : "orange";

const AdminDash = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    appointments: { total: 0, completed: 0, pending: 0 },
    tickets: { total: 0, completed: 0, open: 0 },
    ticketByStatus: [],
    ticketByPriority: [],
    ticketByService: [],
    appointmentByStatus: [],
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const uid = localStorage.getItem("uid");
    if (!uid) {
      setError("User ID not found in localStorage.");
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const name = localStorage.getItem("name");
        const token = localStorage.getItem("token");

        // Fetch Admin Details, Dashboard stats, and Recent tickets in parallel
        const [userResponse, statsResponse, recentResponse] = await Promise.all([
          axios.get(`${backendUrl}/user/${uid}`),
          axios.get(`${backendUrl}/admin/dashboard-stats`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { name },
          }),
          axios.get(`${backendUrl}/admin/getalltickets`, {
            headers: { Authorization: `Bearer ${token}` },
            params: { page: 1, limit: 5 },
          }),
        ]);

        setUser(userResponse.data.user?.[0]);
        const s = statsResponse.data.stats || {};
        setStats({
          appointments: s.appointments || { total: 0, completed: 0, pending: 0 },
          tickets: s.tickets || { total: 0, completed: 0, open: 0 },
          ticketByStatus: s.ticketByStatus || [],
          ticketByPriority: s.ticketByPriority || [],
          ticketByService: s.ticketByService || [],
          appointmentByStatus: s.appointmentByStatus || [],
        });
        setRecentTickets(recentResponse.data.tickets || []);
      } catch (err) {
        setError("Error loading data. Please try again.");
        message.error("Error loading dashboard details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    AOS.init({
      duration: 1200,
      once: false,
    });
    AOS.refresh();
  }, [backendUrl]);

  // Re-scan AOS once content is rendered so animated elements become visible
  useEffect(() => {
    if (!loading) {
      AOS.refreshHard();
    }
  }, [loading]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-4 space-y-6">
          <div className="flex items-center space-x-4">
            <Skeleton.Avatar active size="large" />
            <Skeleton.Input active style={{ width: 200 }} />
          </div>
          <Skeleton paragraph={{ rows: 4 }} active />
          <Row gutter={[16, 16]} className="mt-6">
            <Col xs={24} sm={12} lg={12}>
              <Skeleton.Button active block style={{ height: 150 }} />
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Skeleton.Button active block style={{ height: 150 }} />
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Skeleton.Button active block style={{ height: 150 }} />
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Skeleton.Button active block style={{ height: 150 }} />
            </Col>
          </Row>
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="text-center p-6">
          <Text type="danger">{error}</Text>
        </div>
      </AdminLayout>
    );
  }

  const ticketStatusChart = {
    labels: stats.ticketByStatus.map((t) => t.status),
    datasets: [
      {
        data: stats.ticketByStatus.map((t) => t.count),
        backgroundColor: stats.ticketByStatus.map((t) =>
          colorFor(t.status, ticketStatusColors)
        ),
        borderWidth: 1,
      },
    ],
  };

  const ticketPriorityChart = {
    labels: stats.ticketByPriority.map((t) => t.priority),
    datasets: [
      {
        label: "Tickets",
        data: stats.ticketByPriority.map((t) => t.count),
        backgroundColor: "#3b82f6",
        borderRadius: 6,
      },
    ],
  };

  const ticketServiceChart = {
    labels: stats.ticketByService.map((t) => t.service),
    datasets: [
      {
        label: "Tickets",
        data: stats.ticketByService.map((t) => t.count),
        backgroundColor: stats.ticketByService.map(
          (_, i) => PALETTE[i % PALETTE.length]
        ),
        borderRadius: 6,
      },
    ],
  };

  const appointmentStatusChart = {
    labels: stats.appointmentByStatus.map((a) => a.status),
    datasets: [
      {
        data: stats.appointmentByStatus.map((a) => a.count),
        backgroundColor: stats.appointmentByStatus.map((a) =>
          colorFor(a.status, appointmentStatusColors)
        ),
        borderWidth: 1,
      },
    ],
  };

  const recentTicketsColumns = [
    {
      title: "Ticket ID",
      dataIndex: "ticketId",
      key: "ticketId",
      render: (text) => <span>TCK{text}</span>,
    },
    {
      title: "Enrollment",
      dataIndex: "enrollmentId",
      key: "enrollmentId",
      ellipsis: true,
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={statusTagColor(status)}>{status}</Tag>,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => new Date(text).toLocaleDateString(),
    },
  ];

  return (
    <AdminLayout>
      <div className="p-4 space-y-6">
        {user && (
          <>
            <div className="flex items-center space-x-4">
              <UserOutlined className="text-blue-500 text-2xl" />
              <h3
                className="text-3xl font-extrabold text-center"
                style={{
                  background:
                    "linear-gradient(to right, rgba(0, 0, 255, 0.8), rgba(255, 0, 0, 0.8))",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Welcome, {localStorage.getItem("name")}
              </h3>
            </div>
            {/* <Card
              data-aos="fade-up"
              className="shadow-md rounded-lg"
              bordered={false}
            >
              <div className="space-y-2">
                <Text strong>Name:</Text> {user.name}
                <br />
                <Text strong>Email:</Text> {user.email}
                <br />
                <Text strong>UID:</Text> {user.uid || "N/A"}
                <br />
                <Text strong>
                  Joining Date:{" "}
                  {new Date(user.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Text>
              </div>
            </Card> */}
          </>
        )}

        <Row gutter={[16, 16]} className="mt-6">
          {/* Total Appointments */}
          <Col xs={24} sm={12} lg={12}>
            <Card
              data-aos="fade-up"
              onClick={() => {
                navigate("/admin-appointment");
              }}
              className="bg-gradient-to-r from-blue-800 to-blue-400 text-white shadow-lg rounded-lg cursor-pointer transform transition-transform duration-300 hover:scale-105 hover:shadow-2xl hover:brightness-110"
              bordered={false}
            >
              <div className="flex items-center space-x-4">
                <CalendarTodayOutlinedIcon
                  style={{ fontSize: "40px", color: "white" }}
                />
                <div>
                  <Text className="text-lg font-bold block text-white">
                    Total Appointments: {stats.appointments.total}
                  </Text>
                  <Text className="text-sm block text-white">
                    Completed: {stats.appointments.completed}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* Total Tickets */}
          <Col xs={24} sm={12} lg={12}>
            <Card
              data-aos="fade-up"
              onClick={() => {
                navigate("/admin-tickets");
              }}
              className="bg-gradient-to-r from-green-800 to-green-400 text-white shadow-lg rounded-lg cursor-pointer transform transition-transform duration-300 hover:scale-105 hover:shadow-2xl hover:brightness-110"
              bordered={false}
            >
              <div className="flex items-center space-x-4">
                <ConfirmationNumberOutlinedIcon
                  style={{ fontSize: "40px", color: "white" }}
                />
                <div>
                  <Text className="text-lg font-bold block text-white">
                    Total Tickets: {stats.tickets.total}
                  </Text>
                  <Text className="text-sm block text-white">
                    Completed: {stats.tickets.completed}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* Non-Completed Appointments */}
          <Col xs={24} sm={12} lg={12}>
            <Card
              onClick={() => {
                navigate("/admin-appointment");
              }}
              data-aos="fade-up"
              className="bg-gradient-to-r from-orange-800 to-orange-400 text-white shadow-lg rounded-lg cursor-pointer transform transition-transform duration-300 hover:scale-105 hover:shadow-2xl hover:brightness-110"
              bordered={false}
            >
              <div className="flex items-center space-x-4">
                <AssignmentLateOutlinedIcon
                  style={{ fontSize: "40px", color: "white" }}
                />
                <div>
                  <Text className="text-lg font-bold block text-white">
                    Pending Appointments: {stats.appointments.pending}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>

          {/* Open Tickets */}
          <Col xs={24} sm={12} lg={12}>
            <Card
              onClick={() => {
                navigate("/admin-tickets");
              }}
              data-aos="fade-up"
              className="bg-gradient-to-r from-red-800 to-red-400 text-white shadow-lg rounded-lg cursor-pointer transform transition-transform duration-300 hover:scale-105 hover:shadow-2xl hover:brightness-110"
              bordered={false}
            >
              <div className="flex items-center space-x-4">
                <ErrorOutlineOutlinedIcon
                  style={{ fontSize: "40px", color: "white" }}
                />
                <div>
                  <Text className="text-lg font-bold block text-white">
                    Pending Tickets: {stats.tickets.open}
                  </Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>

        <h1 className="inline-block text-3xl font-extrabold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
          Overview
        </h1>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card
              title="Tickets by Status"
              bordered={false}
              className="shadow-md rounded-lg"
            >
              {stats.ticketByStatus.length > 0 ? (
                <div style={{ height: 260 }}>
                  <Doughnut data={ticketStatusChart} options={doughnutOptions} />
                </div>
              ) : (
                <div
                  className="flex items-center justify-center"
                  style={{ height: 260 }}
                >
                  <Text type="secondary">No data available</Text>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              title="Tickets by Service"
              bordered={false}
              className="shadow-md rounded-lg"
            >
              {stats.ticketByService.length > 0 ? (
                <div style={{ height: 260 }}>
                  <Bar data={ticketServiceChart} options={barOptions} />
                </div>
              ) : (
                <div
                  className="flex items-center justify-center"
                  style={{ height: 260 }}
                >
                  <Text type="secondary">No data available</Text>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card
              title="Tickets by Priority"
              bordered={false}
              className="shadow-md rounded-lg"
            >
              {stats.ticketByPriority.length > 0 ? (
                <div style={{ height: 260 }}>
                  <Bar data={ticketPriorityChart} options={barOptions} />
                </div>
              ) : (
                <div
                  className="flex items-center justify-center"
                  style={{ height: 260 }}
                >
                  <Text type="secondary">No data available</Text>
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={8}>
            <Card
              title="Appointments by Status"
              bordered={false}
              className="shadow-md rounded-lg"
            >
              {stats.appointmentByStatus.length > 0 ? (
                <div style={{ height: 260 }}>
                  <Doughnut
                    data={appointmentStatusChart}
                    options={doughnutOptions}
                  />
                </div>
              ) : (
                <div
                  className="flex items-center justify-center"
                  style={{ height: 260 }}
                >
                  <Text type="secondary">No data available</Text>
                </div>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card
              title="Recent Tickets"
              bordered={false}
              className="shadow-md rounded-lg"
              extra={
                <Button
                  type="link"
                  className="font-semibold"
                  onClick={() => navigate("/admin-tickets")}
                >
                  View All
                </Button>
              }
            >
              {recentTickets.length > 0 ? (
                <Table
                  columns={recentTicketsColumns}
                  dataSource={recentTickets}
                  rowKey="ticketId"
                  pagination={false}
                  size="small"
                  scroll={{ x: true }}
                  onRow={(record) => ({
                    onClick: () => navigate("/admin-tickets"),
                  })}
                />
              ) : (
                <p className="text-center text-gray-500 my-4">
                  No recent tickets.
                </p>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </AdminLayout>
  );
};

export default AdminDash;
